import { useContext } from 'react';
import { Campaign, Client, Collab } from '@foodfluence/shared';
import { CollabDataContext } from 'contexts/collabDataContext';
import { InfluencerCampaignDataContext } from 'contexts/InfluencerCampaignDataContext';
import { ClientDataContext } from 'contexts/ClientDataContext';
import { useMarketplaceOrders } from 'contexts/MarketplaceOrdersContext';

export interface ConversationEntityState {
  collab?: Collab;
  campaign?: Campaign;
  client?: Client;
  marketplaceOrderId?: string;
}

export interface ConversationEntities extends ConversationEntityState {
  /** true once the id matched a collab or a marketplace order */
  isResolved: boolean;
}

interface EntitySources {
  collabsData: Collab[];
  influencerCampaignData: Record<string, Campaign>; // keyed by collab id
  clientData: Record<string, Client>;
  marketplaceOrders: Array<{ id: string }>;
}

/**
 * Resolve the entities a conversation needs (collab/campaign/client or marketplace
 * order) from an id. Router state — when present from an in-app click — wins as a
 * warm-start; context lookups cover email deep links and hard refreshes.
 */
export function resolveConversationEntities(
  conversationId: string | undefined,
  state: ConversationEntityState | null,
  sources: EntitySources
): ConversationEntities {
  if (!conversationId) {
    return { isResolved: false };
  }
  // Live data wins over the navigation snapshot, not the other way round.
  //
  // `state.collab` is whatever the collab looked like when the thread was opened, and it never
  // updates again — while CollabDataContext is an onSnapshot listener that does. Preferring the
  // snapshot meant the thread kept rendering against a collab frozen minutes ago: the inbox
  // cancel agent's Undo chip is gated on `status === 'collabCanceled'`, so a creator who
  // cancelled from within the thread was shown no way to undo it, because their own client still
  // believed the collab was pendingRedemption. The same staleness hid the Reschedule chip after
  // a visit date changed.
  //
  // The snapshot stays as the fallback: it is what lets the page render before the listener has
  // loaded, which is the job it was actually there to do.
  const collab = sources.collabsData.find((c) => c.id === conversationId) ?? state?.collab;
  const marketplaceOrderId =
    state?.marketplaceOrderId ?? sources.marketplaceOrders.find((o) => o.id === conversationId)?.id;
  const campaign = state?.campaign ?? (collab ? sources.influencerCampaignData[collab.id] : undefined);
  const clientId = campaign?.clientID ?? collab?.clientID;
  const client = state?.client ?? (clientId ? sources.clientData[clientId] : undefined);
  return { collab, campaign, client, marketplaceOrderId, isResolved: Boolean(collab || marketplaceOrderId) };
}

export function useConversationEntities(
  conversationId: string | undefined,
  state: ConversationEntityState | null
): ConversationEntities {
  const collabsData = useContext(CollabDataContext);
  const influencerCampaignData = useContext(InfluencerCampaignDataContext);
  const clientData = useContext(ClientDataContext);
  const { orders: marketplaceOrders } = useMarketplaceOrders();
  return resolveConversationEntities(conversationId, state, {
    collabsData,
    influencerCampaignData,
    clientData,
    marketplaceOrders,
  });
}
